import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Modal, Typography, TextField, Divider, Alert } from '@mui/material';
import { BookmarkBorder } from '@mui/icons-material';
import ActionButton from './ActionButton.jsx';
import { PALETTE, BORDERS } from '../theme';
import { useAuth } from '../auth/AuthContext';
import { createMap, updateMap } from '../utils/mapsRepository';

/**
 * One Save control for every tool. The tool supplies `getSnapshot()` (async →
 * { type, defaultName, breadcrumb, settings, assets, thumbnailBlob }) and, when
 * a saved map is currently open, `openMap` ({ id, name }). On success it calls
 * `onSaved({ id, name })` so the tool can track the open map for later overwrites.
 *
 * Behaviour:
 *   - logged out → opens the global auth dialog first (requireAccount)
 *   - fresh map  → name dialog, creates a new map
 *   - open map   → choose Overwrite, or save as a new copy under a new name (req #4)
 */
const modalCardSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420,
    maxWidth: '92vw',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(10px)',
    border: BORDERS.soft,
    borderRadius: 3,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    p: { xs: 3, sm: 4 },
};

const SaveMapButton = ({
    getSnapshot,
    openMap = null,
    onSaved,
    onStatus,
    disabled = false,
    variant = 'ghost',
    sx,
}) => {
    const { requireAccount } = useAuth();
    const [open, setOpen] = useState(false);
    const [snapshot, setSnapshot] = useState(null);
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [gathering, setGathering] = useState(false);
    const [error, setError] = useState(null);

    const close = () => {
        setOpen(false);
        setSnapshot(null);
        setError(null);
        setBusy(false);
    };

    const handleClick = async () => {
        setError(null);
        const ok = await requireAccount();
        if (!ok) return; // user dismissed the sign-in dialog
        setGathering(true);
        try {
            const snap = await getSnapshot();
            setSnapshot(snap);
            // Fresh map → prefill suggested name; copy path → "<name> copy".
            setName(openMap ? `${openMap.name} copy` : snap.defaultName || 'My map');
            setOpen(true);
        } catch (err) {
            if (onStatus) onStatus('Could not prepare the map to save. Please try again.', 'error');
            // eslint-disable-next-line no-console
            console.error('getSnapshot failed:', err);
        } finally {
            setGathering(false);
        }
    };

    const runSave = async (fn, savedName) => {
        setBusy(true);
        setError(null);
        try {
            const result = await fn();
            close();
            if (onSaved) onSaved(result);
            if (onStatus) onStatus(`Saved “${savedName}” to your maps.`, 'success');
        } catch (err) {
            setError(err.message || 'Could not save. Please try again.');
            setBusy(false);
        }
    };

    const handleOverwrite = () =>
        runSave(() => updateMap(openMap.id, snapshot, openMap.name), openMap.name);

    const handleSaveNew = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        runSave(() => createMap(snapshot, trimmed), trimmed);
    };

    return (
        <>
            <ActionButton
                variant={variant}
                startIcon={<BookmarkBorder sx={{ fontSize: 18 }} />}
                onClick={handleClick}
                disabled={disabled}
                loading={gathering}
                sx={sx}
            >
                Save
            </ActionButton>

            <Modal open={open} onClose={busy ? undefined : close}>
                <Box sx={modalCardSx}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: PALETTE.text, mb: 0.5 }}>
                        {openMap ? 'Save changes' : 'Save to My Maps'}
                    </Typography>
                    {snapshot && snapshot.breadcrumb && snapshot.breadcrumb.length > 0 && (
                        <Typography variant="body2" sx={{ color: PALETTE.textMuted, mb: 2 }}>
                            {snapshot.breadcrumb.join(' › ')}
                        </Typography>
                    )}

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {openMap ? (
                        <>
                            <ActionButton
                                variant="primary"
                                onClick={handleOverwrite}
                                loading={busy}
                                sx={{ width: '100%' }}
                            >
                                Overwrite “{openMap.name}”
                            </ActionButton>

                            <Divider sx={{ my: 2.5, color: PALETTE.textFaint, fontSize: '0.8rem' }}>
                                or save as a new map
                            </Divider>

                            <TextField
                                label="New map name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={busy}
                                inputProps={{ maxLength: 60 }}
                            />
                            <ActionButton
                                variant="secondary"
                                onClick={handleSaveNew}
                                disabled={busy || !name.trim()}
                                sx={{ width: '100%', mt: 1.5 }}
                            >
                                Save as new
                            </ActionButton>
                        </>
                    ) : (
                        <>
                            <TextField
                                label="Map name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                size="small"
                                autoFocus
                                disabled={busy}
                                inputProps={{ maxLength: 60 }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveNew();
                                }}
                            />
                            <ActionButton
                                variant="primary"
                                onClick={handleSaveNew}
                                loading={busy}
                                disabled={!name.trim()}
                                sx={{ width: '100%', mt: 1.5 }}
                            >
                                Save
                            </ActionButton>
                        </>
                    )}

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <ActionButton variant="ghost" onClick={close} disabled={busy} sx={{ minWidth: 0 }}>
                            Cancel
                        </ActionButton>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

SaveMapButton.propTypes = {
    getSnapshot: PropTypes.func.isRequired,
    openMap: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    onSaved: PropTypes.func,
    onStatus: PropTypes.func,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(['primary', 'secondary', 'dark', 'ghost']),
    sx: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default SaveMapButton;
